import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ChevronUp, Lightbulb, Lock, ShieldCheck, Plus, X, Users, Zap, Plane, Hotel, Car, FileText as FileIcon, Map as MapIcon, Shield, ClipboardList } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PreviewCard } from "@/components/DocumentMockups";
import { ADD_ONS } from "@/lib/addons";
import { BUILD_DOCS, BUILD_DOCS_BY_ID, suggestPackage } from "@/lib/buildDocs";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhoneInput from "@/components/PhoneInput";
import DateInput, { isoToDisplay } from "@/components/DateInput";
import AddOnDetailsForm from "@/components/AddOnDetailsForm";
import AirportInput from "@/components/AirportInput";
import { findAirportByValue } from "@/lib/airports";
import { SCHENGEN_COUNTRIES } from "@/lib/countries";
import { WORLD_COUNTRIES } from "@/lib/worldCountries";
import { CountryCombobox } from "@/components/CountryCombobox";
import { SEO } from "@/components/SEO";
import { openPaddleCheckout } from "@/lib/paddle";

const EXPRESS_PER_DOC = 10;

const ADDON_EMOJI = { noc: "📋", sponsor_letter: "👥", invitation_letter: "🏠" };
const ADDON_DESC = {
  noc: "Employer letter confirming employment and granting travel permission",
  sponsor_letter: "Formal letter from your financial sponsor covering travel and living costs",
  invitation_letter: "Letter from your host in the Schengen area inviting you to visit",
};
const ADDON_PREVIEWS = {
  noc:               { title: "No Objection Certificate",        package_contents: "Employer authorisation" },
  sponsor_letter:    { title: "Sponsor Letter",                  package_contents: "Financial sponsor declaration" },
  invitation_letter: { title: "Invitation Letter",               package_contents: "Host invitation" },
};

function blankInfo() {
  return {
    full_name: "",
    email: "",
    whatsapp: "",
    applying_from_country: "",
    destination_country: "",
    notes: "",
    travellers: [{ name: "", dob: "" }],
    flight: { trip_type: "round", from: "", to: "", departure_date: "", return_date: "", legs: [{ from: "", to: "", date: "" }] },
    hotel: { entries: [{ city: "", check_in: "", check_out: "", preferences: "" }] },
    car: { pickup_city: "", pickup_date: "", dropoff_date: "", driver_name: "" },
    cover: { purpose: "", other_purpose: "", employment_status: "", travel_from: "", travel_to: "" },
    itinerary: { entries: [{ city: "", date_mode: "arrange", from: "", to: "" }], activities: "" },
    insurance: { dobs: [""] },
  };
}

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function nightsBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const n = Math.round((new Date(toIso) - new Date(fromIso)) / 86400000);
  return n > 0 ? n : 0;
}

function flightDateRange(flight) {
  if (!flight) return { start: "", end: "" };
  if (flight.trip_type === "multi") {
    const dates = (flight.legs || []).map((l) => l?.date).filter(Boolean).sort();
    return { start: dates[0] || "", end: dates[dates.length - 1] || "" };
  }
  return { start: flight.departure_date || "", end: flight.return_date || "" };
}

function SoftWarning({ children, confirmLabel = "Yes, that's correct", changeLabel, onConfirm, onChange, testId }) {
  return (
    <div data-testid={testId} className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <div className="flex items-start gap-2">
        <span aria-hidden>⚠️</span>
        <div className="flex-1">
          <div>{children}</div>
          <div className="mt-1.5 flex gap-2 flex-wrap">
            <button type="button" data-testid={`${testId}-confirm`} onClick={onConfirm} className="px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold">{confirmLabel}</button>
            {changeLabel && (
              <button type="button" data-testid={`${testId}-change`} onClick={onChange} className="px-2 py-1 rounded border border-amber-400 bg-white hover:bg-amber-100 text-amber-900 font-semibold">{changeLabel}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Round-trip only: the last city (by latest date, else listed order) should equal the
// flight destination city — otherwise the customer ends their trip away from the return airport.
function lastCityMismatch(entries, dateKey, flight) {
  if (!flight || flight.trip_type !== "round") return null;
  const destAir = findAirportByValue(flight.to);
  if (!destAir) return null;
  const withCity = (entries || []).filter((e) => (e.city || "").trim());
  if (withCity.length === 0) return null;
  const dated = withCity.filter((e) => e[dateKey]);
  const last = dated.length > 0
    ? dated.reduce((a, b) => (b[dateKey] > a[dateKey] ? b : a))
    : withCity[withCity.length - 1];
  const lastCity = (last.city || "").split("–")[0].trim();
  if (!lastCity || lastCity.toLowerCase() === destAir.city.toLowerCase()) return null;
  return { lastCity, destCity: destAir.city, key: `last:${lastCity}:${destAir.city}` };
}

function MatchedBanner({ children, testId }) {
  return (
    <div data-testid={testId} className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-start gap-2">
      <span aria-hidden>✅</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function ageCategory(dobIso) {
  if (!dobIso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dobIso);
  if (!m) return null;
  const dob = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const md = today.getMonth() - dob.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < dob.getDate())) age--;
  if (age < 0) return null;
  if (age < 2) return "Infant";
  if (age < 12) return "Child";
  return "Adult";
}

const CAT_STYLES = {
  Adult: "bg-blue-100 text-blue-700",
  Child: "bg-green-100 text-green-700",
  Infant: "bg-violet-100 text-violet-700",
};

function CategoryBadge({ category, testId }) {
  return (
    <span data-testid={testId} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${CAT_STYLES[category] || CAT_STYLES.Adult}`}>
      {category}
    </span>
  );
}

export default function BuildYourFile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState("select"); // "select" | "info"
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [isExpress, setIsExpress] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [info, setInfo] = useState(blankInfo());
  const [addOnDetails, setAddOnDetails] = useState({});
  const applicants = (info.travellers || []).length || 1;

  // Prefill name/email from user once available
  useEffect(() => {
    if (user) {
      setInfo((prev) => ({
        ...prev,
        full_name: prev.full_name || user.name || "",
        email: prev.email || user.email || "",
      }));
    }
  }, [user]);

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, []);
  // Scroll to top when step changes
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "smooth" }); }, [step]);

  // Keep insurance DOBs array sized to applicants
  useEffect(() => {
    setInfo((prev) => {
      const dobs = [...(prev.insurance?.dobs || [])];
      while (dobs.length < applicants) dobs.push("");
      while (dobs.length > applicants) dobs.pop();
      return { ...prev, insurance: { ...prev.insurance, dobs } };
    });
  }, [applicants]);

  const toggleDoc = (id) => setSelectedDocs((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAddon = (id) => setSelectedAddons((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const docsPerApplicant = useMemo(() => selectedDocs.reduce((s, id) => s + (BUILD_DOCS_BY_ID[id]?.price || 0), 0), [selectedDocs]);
  const addonsPerApplicant = useMemo(() => selectedAddons.length * 19, [selectedAddons]);
  const docsSubtotal = docsPerApplicant * applicants;
  const addonsSubtotal = addonsPerApplicant * applicants;
  const expressSurcharge = isExpress ? selectedDocs.length * EXPRESS_PER_DOC * applicants : 0;
  const total = docsSubtotal + addonsSubtotal + expressSurcharge;
  const suggested = useMemo(() => (selectedDocs.length >= 3 ? suggestPackage(selectedDocs, docsPerApplicant) : null), [selectedDocs, docsPerApplicant]);

  const canContinue = selectedDocs.length > 0 || selectedAddons.length > 0;

  const goToInfo = () => {
    if (!canContinue) return;
    setSubmitError("");
    if (!user) {
      const next = encodeURIComponent("/build-your-file");
      navigate(`/login?next=${next}`);
      return;
    }
    setStep("info");
  };

  const setAddOnDetailsSection = (key, patch) => {
    setAddOnDetails((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };

  const validateInfo = () => {
    if (!info.full_name.trim()) return "Full name is required";
    if (!info.email.trim()) return "Email is required";
    if (!info.whatsapp || info.whatsapp.replace(/\D/g, "").length < 7) return "WhatsApp number is required";
    const trav = info.travellers || [];
    if (trav.length === 0) return "Travellers: add at least one traveller";
    for (let i = 0; i < trav.length; i++) {
      if (!trav[i].name.trim()) return `Traveller ${i + 1}: full name is required`;
      if (!trav[i].dob) return `Traveller ${i + 1}: date of birth is required`;
    }
    if (!info.applying_from_country) return "Where you are applying from is required";
    if (!info.destination_country) return "Destination country is required";
    // Section-specific quick checks
    if (selectedDocs.includes("flight")) {
      if (info.flight.trip_type === "multi") {
        const filled = info.flight.legs.filter((l) => l.from && l.to && l.date);
        if (filled.length < 1) return "Flight: please add at least one leg";
      } else {
        if (!info.flight.from || !info.flight.to || !info.flight.departure_date) return "Flight: from/to/departure date are required";
        if (info.flight.trip_type === "round" && !info.flight.return_date) return "Flight: return date is required";
      }
    }
    if (selectedDocs.includes("hotel")) {
      const entries = info.hotel?.entries || [];
      if (entries.length === 0) return "Hotel: please add at least one city";
      const ok = entries.every((e) => e.city && e.check_in && e.check_out);
      if (!ok) return "Hotel: each entry needs city, check-in and check-out dates";
    }
    if (selectedDocs.includes("car")) {
      if (!info.car.pickup_city || !info.car.pickup_date || !info.car.dropoff_date) return "Car rental: city + pick-up/drop-off dates are required";
    }
    if (selectedDocs.includes("cover")) {
      if (!info.cover.purpose || !info.cover.employment_status || !info.cover.travel_from || !info.cover.travel_to) return "Cover letter: all fields are required";
    }
    if (selectedDocs.includes("itinerary")) {
      const entries = info.itinerary?.entries || [];
      if (entries.length === 0 || entries.some((e) => !e.city)) return "Itinerary: each city entry needs a city";
      if (entries.some((e) => e.date_mode === "custom" && (!e.from || !e.to))) return "Itinerary: set both dates or choose 'SchengenFile will arrange'";
    }
    if (selectedDocs.includes("insurance")) {
      const missing = info.insurance.dobs.some((d) => !d);
      if (missing) return "Insurance: date of birth required for every applicant";
    }
    return null;
  };

  const proceedToPayment = async () => {
    setSubmitError("");
    const err = validateInfo();
    if (err) { setSubmitError(err); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setSubmitting(true);
    try {
      const travellersOut = (info.travellers || []).map((t) => ({
        name: t.name.trim(),
        dob: isoToDisplay(t.dob),
        category: ageCategory(t.dob) || "Adult",
      }));
      const customFormData = { ...info, travellers: travellersOut, isExpress };
      const payload = {
        order_type: "custom",
        full_name: info.full_name,
        email: info.email,
        whatsapp: info.whatsapp,
        num_applicants: applicants,
        package_id: "custom",
        delivery_type: isExpress ? "express" : "normal",
        selected_documents: selectedDocs,
        add_ons: selectedAddons,
        add_on_details: addOnDetails,
        custom_form_data: customFormData,
        applying_from_country: info.applying_from_country,
        destination_country: info.destination_country,
        notes: info.notes,
      };
      const { data: order } = await api.post("/orders", payload);
      const origin = window.location.origin;
      const { data: session } = await api.post("/payments/checkout", { order_id: order.id, origin_url: origin });
      // Paddle Billing: open the overlay against the server-created transaction.
      // The webhook flips the order to `paid`; the success URL then routes the
      // user to the applicant wizard, which polls /payments/status until paid.
      await openPaddleCheckout({
        transactionId: session.transaction_id || session.session_id,
        successUrl: `${origin}/payment/success?session_id=${encodeURIComponent(session.transaction_id || session.session_id)}&order_id=${encodeURIComponent(order.id)}`,
        customerEmail: user?.email,
      });
      setSubmitting(false);
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || "Failed to create order. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#0f1b2d]">
      <SEO
        title="Build Your Schengen Visa File — Pay Per Document | SchengenFile"
        description="Pick only the visa documents you need — flight PNR, hotel booking, insurance, cover letter and more. Custom pricing, delivered in 24 hours."
      />
      <Header />

      <main>
      {/* SECTION 1 — Page header */}
      <section className="relative py-16 sm:py-20 bg-[#0f1b2d] overflow-hidden" data-testid="byf-header">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1b2d] via-[#0f1b2d] to-[#1e3a8a]/70" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center text-white">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#60a5fa]">Custom Package</div>
          <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl lg:text-6xl">Build Your Visa File</h1>
          <p className="mt-4 text-base sm:text-lg text-white/75 max-w-2xl mx-auto">Select only the documents you need. Pay per document — or save more with a package.</p>
          <Link
            to="/pricing"
            data-testid="byf-tip-banner"
            className="mt-6 inline-flex items-center gap-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 text-sm font-medium px-4 py-2.5 rounded-md transition"
          >
            <Lightbulb className="w-4 h-4 text-amber-600" />
            <span><span className="font-semibold">Tip:</span> Packages are better value. Basic $39 · Complete $69 · Premium $99 — <span className="font-semibold underline">See packages →</span></span>
          </Link>
        </div>
      </section>

      <section className="py-16 pb-28 md:pb-16 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT */}
          <div className="lg:col-span-8">
            {step === "select" && (
              <SelectorStep
                selectedDocs={selectedDocs}
                selectedAddons={selectedAddons}
                toggleDoc={toggleDoc}
                toggleAddon={toggleAddon}
                docsPerApplicant={docsPerApplicant}
                suggested={suggested}
              />
            )}
            {step === "info" && (
              <InfoStep
                info={info}
                setInfo={setInfo}
                selectedDocs={selectedDocs}
                selectedAddons={selectedAddons}
                addOnDetails={addOnDetails}
                setAddOnDetailsSection={setAddOnDetailsSection}
                onBack={() => setStep("select")}
              />
            )}
          </div>

          {/* RIGHT: live order summary */}
          <div className="lg:col-span-4">
            <OrderSummary
              step={step}
              selectedDocs={selectedDocs}
              selectedAddons={selectedAddons}
              applicants={applicants}
              travellers={info.travellers || []}
              isExpress={isExpress}
              setIsExpress={setIsExpress}
              docsPerApplicant={docsPerApplicant}
              addonsPerApplicant={addonsPerApplicant}
              docsSubtotal={docsSubtotal}
              addonsSubtotal={addonsSubtotal}
              expressSurcharge={expressSurcharge}
              total={total}
              canContinue={canContinue}
              submitting={submitting}
              submitError={submitError}
              onContinue={goToInfo}
              onProceed={proceedToPayment}
              hotelCityCount={(info.hotel?.entries || []).filter((e) => e.city).length}
            />
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}

/* ==================== Step 1 — Selector ==================== */
function SelectorStep({ selectedDocs, selectedAddons, toggleDoc, toggleAddon, docsPerApplicant, suggested }) {
  return (
    <section aria-labelledby="byf-select-heading">
      <header className="mb-6">
        <h2 id="byf-select-heading" className="font-display font-bold text-2xl sm:text-3xl text-[#0f1b2d]">Select your documents</h2>
        <p className="mt-2 text-[15px] text-[#475569] leading-relaxed max-w-2xl">
          Select the documents you need for your Schengen visa application. Each document is prepared by our expert team and delivered within 24 hours. Select one or more — your total updates instantly. Not sure what you need? Our packages offer better value:{" "}
          <Link to="/pricing" data-testid="byf-link-basic" className="text-[#2563eb] hover:underline font-semibold">Basic $39</Link>
          {" · "}
          <Link to="/pricing" data-testid="byf-link-complete" className="text-[#2563eb] hover:underline font-semibold">Complete $69</Link>
          {" · "}
          <Link to="/pricing" data-testid="byf-link-premium" className="text-[#2563eb] hover:underline font-semibold">Premium $99</Link>
        </p>
      </header>

      {/* Unified 10-card grid: 7 docs + 3 add-ons */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="byf-doc-grid">
        {BUILD_DOCS.map((d) => (
          <DocSelectorCard
            key={d.id}
            testIdRoot={`byf-doc-${d.id}`}
            checked={selectedDocs.includes(d.id)}
            onToggle={() => toggleDoc(d.id)}
            emoji={d.emoji}
            name={d.name}
            description={d.description}
            price={d.price}
            preview={d.preview}
          />
        ))}
        {ADD_ONS.map((a) => (
          <DocSelectorCard
            key={a.id}
            testIdRoot={`byf-addon-${a.id}`}
            checked={selectedAddons.includes(a.id)}
            onToggle={() => toggleAddon(a.id)}
            emoji={ADDON_EMOJI[a.id]}
            name={a.name}
            description={ADDON_DESC[a.id]}
            price={a.price}
            preview={ADDON_PREVIEWS[a.id]}
          />
        ))}
      </div>

      {/* Package hint */}
      {suggested && (
        <div data-testid="byf-package-hint" className="mt-6 rounded-lg bg-[#eff6ff] border border-[#bfdbfe] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <Lightbulb className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
          <div className="flex-1 text-sm text-[#0f1b2d]">
            You&apos;ve selected <span className="font-semibold">{selectedDocs.length}</span> documents for <span className="font-semibold">${docsPerApplicant}</span>. The <span className="font-semibold">{suggested.name}</span> package includes all of these for only <span className="font-semibold">${suggested.price}</span> — saving you <span className="font-semibold text-[#16a34a]">${suggested.saving}</span>!
          </div>
          <Link
            to={`/apply?package=${suggested.name.toLowerCase()}`}
            data-testid="byf-switch-package-btn"
            className="inline-flex items-center justify-center gap-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold px-4 py-2 rounded-md whitespace-nowrap"
          >
            Switch to {suggested.name} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </section>
  );
}

/* ==================== Step 2 — Info form ==================== */
function InfoStep({ info, setInfo, selectedDocs, selectedAddons, addOnDetails, setAddOnDetailsSection, onBack }) {
  const patch = (k, v) => setInfo((p) => ({ ...p, [k]: v }));
  const patchSection = (sec, p) => setInfo((prev) => ({ ...prev, [sec]: { ...prev[sec], ...p } }));
  const travellers = info.travellers || [];

  return (
    <section className="space-y-6" data-testid="byf-info-step" aria-labelledby="byf-info-heading">
      <button type="button" data-testid="byf-info-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f1b2d]">
        <ArrowLeft className="w-4 h-4" /> Back to documents
      </button>
      <h2 id="byf-info-heading" className="sr-only">Your travel details</h2>

      <FormSection title="Your details" icon={ClipboardList} subtitle="Contact information for your file">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name" required>
            <input type="text" data-testid="byf-info-name" className="byf-inp" value={info.full_name} onChange={(e) => patch("full_name", e.target.value)} />
          </Field>
          <Field label="Email" required>
            <input type="email" data-testid="byf-info-email" className="byf-inp" value={info.email} onChange={(e) => patch("email", e.target.value)} />
          </Field>
          <Field label="WhatsApp number" required>
            <PhoneInput value={info.whatsapp} onChange={(v) => patch("whatsapp", v)} testId="byf-info-whatsapp" />
          </Field>
          <Field label="Where are you applying from?" required hint="Country of your visa appointment">
            <CountryCombobox
              options={WORLD_COUNTRIES}
              value={info.applying_from_country}
              onChange={(v) => patch("applying_from_country", v)}
              placeholder="Select…"
              testId="byf-info-applying-from"
            />
          </Field>
          <Field label="Destination country" required>
            <CountryCombobox
              options={SCHENGEN_COUNTRIES}
              value={info.destination_country}
              onChange={(v) => patch("destination_country", v)}
              valueField="name"
              placeholder="Select…"
              testId="byf-info-destination"
            />
          </Field>
        </div>
        <Field label="Notes" hint="Optional">
          <textarea rows={2} data-testid="byf-info-notes" className="byf-inp" value={info.notes} onChange={(e) => patch("notes", e.target.value)} />
        </Field>
      </FormSection>

      <TravellersSection travellers={travellers} setTravellers={(next) => patch("travellers", next)} />

      {selectedDocs.includes("flight") && (
        <FormSection title="Flight Details" icon={Plane}>
          <FlightFields data={info.flight} setData={(p) => patchSection("flight", p)} applyingFromCountry={info.applying_from_country} destinationCountry={info.destination_country} />
          <PassengersList travellers={travellers} testId="byf-flight-passengers" />
        </FormSection>
      )}

      {selectedDocs.includes("hotel") && (
        <FormSection title="Hotel Details" icon={Hotel}>
          <HotelFields
            data={info.hotel}
            setData={(p) => patchSection("hotel", p)}
            flight={selectedDocs.includes("flight") ? info.flight : null}
          />
          <PassengersList travellers={travellers} testId="byf-hotel-passengers" />
        </FormSection>
      )}

      {selectedDocs.includes("car") && (
        <FormSection title="Car Rental Details" icon={Car}>
          <CarFields data={info.car} setData={(p) => patchSection("car", p)} fallbackDriver={info.full_name} />
        </FormSection>
      )}

      {selectedDocs.includes("cover") && (
        <FormSection title="Cover Letter Details" icon={FileIcon}>
          <CoverFields data={info.cover} setData={(p) => patchSection("cover", p)} />
        </FormSection>
      )}

      {selectedDocs.includes("itinerary") && (
        <FormSection title="Itinerary Details" icon={MapIcon}>
          <ItineraryFields
            data={info.itinerary}
            setData={(p) => patchSection("itinerary", p)}
            flight={selectedDocs.includes("flight") ? info.flight : null}
            hotel={selectedDocs.includes("hotel") ? info.hotel : null}
          />
        </FormSection>
      )}

      {selectedDocs.includes("insurance") && (
        <FormSection title="Insurance Details" icon={Shield} subtitle="Date of birth needed for every applicant">
          <InsuranceFields dobs={info.insurance.dobs} setDob={(i, v) => {
            const next = [...info.insurance.dobs]; next[i] = v;
            patchSection("insurance", { dobs: next });
          }} />
        </FormSection>
      )}

      {selectedDocs.includes("visa") && (
        <div data-testid="byf-visa-info" className="rounded-lg border border-[#bfdbfe] bg-[#eff6ff] p-4 text-sm text-[#0f1b2d] flex items-start gap-3">
          <ClipboardList className="w-5 h-5 text-[#2563eb] mt-0.5 flex-shrink-0" />
          <span>You&apos;ve selected <span className="font-semibold">Visa Application Form</span>. After payment, you&apos;ll be taken to a short applicant wizard (passport, application, travel and additional details).</span>
        </div>
      )}

      {selectedAddons.length > 0 && (
        <AddOnDetailsForm addOns={selectedAddons} details={addOnDetails} setSection={setAddOnDetailsSection} />
      )}

      <style>{`.byf-inp { width:100%; border:1px solid #e2e8f0; background:#fff; color:#0f1b2d; border-radius:6px; padding:10px 12px; outline:none; font-size:14px; }
        .byf-inp:focus { border-color:#2563eb; box-shadow:0 0 0 3px rgba(37,99,235,0.15); }`}</style>
    </section>
  );
}

function TravellersSection({ travellers, setTravellers }) {
  const setT = (i, p) => setTravellers(travellers.map((t, idx) => (idx === i ? { ...t, ...p } : t)));
  const add = () => { if (travellers.length < 9) setTravellers([...travellers, { name: "", dob: "" }]); };
  const remove = (i) => { if (travellers.length > 1) setTravellers(travellers.filter((_, idx) => idx !== i)); };
  return (
    <FormSection title="Travellers" icon={Users} subtitle="Add details for each traveller">
      <div className="space-y-4" data-testid="byf-travellers">
        {travellers.map((t, i) => {
          const cat = ageCategory(t.dob);
          return (
            <div key={i} className="rounded-md border border-[#e2e8f0] p-3" data-testid={`byf-traveller-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-[#0f1b2d]">Traveller {i + 1}</span>
                {cat && <CategoryBadge category={cat} testId={`byf-traveller-${i}-badge`} />}
                {travellers.length > 1 && (
                  <button type="button" data-testid={`byf-traveller-${i}-remove`} onClick={() => remove(i)} aria-label={`Remove traveller ${i + 1}`} className="ml-auto text-[#dc2626] hover:bg-red-50 rounded p-1">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <input data-testid={`byf-traveller-${i}-name`} className="byf-inp" value={t.name} onChange={(e) => setT(i, { name: e.target.value })} />
                </Field>
                <Field label="Date of birth" required>
                  <DateInput value={t.dob} onChange={(v) => setT(i, { dob: v })} testId={`byf-traveller-${i}-dob`} />
                </Field>
              </div>
            </div>
          );
        })}
        {travellers.length < 9 && (
          <button type="button" data-testid="byf-traveller-add" onClick={add} className="inline-flex items-center gap-1.5 text-sm text-[#2563eb] hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add traveller
          </button>
        )}
      </div>
    </FormSection>
  );
}

function PassengersList({ travellers, testId }) {
  return (
    <div data-testid={testId} className="pt-3 border-t border-dashed border-[#e2e8f0]">
      <div className="text-xs font-semibold text-[#0f1b2d] mb-2">Passengers</div>
      <ul className="space-y-1.5">
        {(travellers || []).map((t, i) => {
          const cat = ageCategory(t.dob) || "Adult";
          const name = (t.name || "").trim() || `Traveller ${i + 1}`;
          return (
            <li key={i} className="flex items-center gap-2 text-sm text-[#0f1b2d]" data-testid={`${testId}-item-${i}`}>
              <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" strokeWidth={3} />
              <span className="truncate">{name}</span>
              <span className="text-[#94a3b8]">—</span>
              <CategoryBadge category={cat} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FlightFields({ data, setData, applyingFromCountry, destinationCountry }) {
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const [warnDismissed, setWarnDismissed] = useState(false);
  const [destWarnDismissed, setDestWarnDismissed] = useState(false);

  // Detect mismatch: airport country (mapped via WORLD_COUNTRIES name → ISO-2) ≠ applying_from_country
  const mismatch = useMemo(() => {
    if (!applyingFromCountry || !data.from) return null;
    const air = findAirportByValue(data.from);
    if (!air) return null;
    const wc = WORLD_COUNTRIES.find((c) => c.name === air.country);
    if (!wc) return null;
    if (wc.code === applyingFromCountry) return null;
    return { airport: air, airportCountry: air.country };
  }, [applyingFromCountry, data.from]);

  const applyingFromLabel = useMemo(() => {
    if (!applyingFromCountry) return "";
    const c = WORLD_COUNTRIES.find((x) => x.code === applyingFromCountry);
    return c ? c.name : applyingFromCountry;
  }, [applyingFromCountry]);

  // Destination mismatch: flight destination airport country ≠ selected destination country (name)
  const destMismatch = useMemo(() => {
    if (!destinationCountry || !data.to) return null;
    const air = findAirportByValue(data.to);
    if (!air) return null;
    if (air.country === destinationCountry) return null;
    return { airportCountry: air.country };
  }, [destinationCountry, data.to]);

  const setLeg = (i, p) => {
    const legs = [...data.legs]; legs[i] = { ...legs[i], ...p }; setData({ legs });
  };
  const addLeg = () => { if (data.legs.length < 3) setData({ legs: [...data.legs, { from: "", to: "", date: "" }] }); };
  const removeLeg = (i) => { setData({ legs: data.legs.filter((_, idx) => idx !== i) }); };

  return (
    <>
      <Field label="Trip type" required>
        <select data-testid="byf-flight-trip-type" className="byf-inp" value={data.trip_type} onChange={(e) => setData({ trip_type: e.target.value })}>
          <option value="round">Round Trip</option>
          <option value="oneway">One Way</option>
          <option value="multi">Multi-City</option>
        </select>
      </Field>
      {data.trip_type !== "multi" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Field label="Departure city/airport" required>
              <div ref={fromRef}>
                <AirportInput value={data.from} onChange={(v) => { setData({ from: v }); setWarnDismissed(false); }} testId="byf-flight-from" placeholder="Riyadh – RUH" />
              </div>
            </Field>
            {mismatch && !warnDismissed && (
              <DepartureMismatchWarning
                applyingFromLabel={applyingFromLabel}
                onDismiss={() => setWarnDismissed(true)}
                onChange={() => {
                  setData({ from: "" });
                  setWarnDismissed(true);
                  // focus the input
                  setTimeout(() => {
                    const inp = fromRef.current?.querySelector("input");
                    if (inp) inp.focus();
                  }, 0);
                }}
              />
            )}
          </div>
          <div>
            <Field label="Destination city/airport" required>
              <div ref={toRef}>
                <AirportInput value={data.to} onChange={(v) => { setData({ to: v }); setDestWarnDismissed(false); }} testId="byf-flight-to" placeholder="Paris – CDG" />
              </div>
            </Field>
            {destMismatch && !destWarnDismissed && (
              <SoftWarning
                testId="byf-flight-destination-mismatch"
                confirmLabel="Yes, continue"
                changeLabel="Change destination"
                onConfirm={() => setDestWarnDismissed(true)}
                onChange={() => {
                  setData({ to: "" });
                  setDestWarnDismissed(true);
                  setTimeout(() => {
                    const inp = toRef.current?.querySelector("input");
                    if (inp) inp.focus();
                  }, 0);
                }}
              >
                Your flight destination ({destMismatch.airportCountry}) doesn&apos;t match your selected destination country ({destinationCountry}). Is this correct?
              </SoftWarning>
            )}
          </div>
          <Field label="Departure date" required>
            <DateInput value={data.departure_date} onChange={(v) => setData({ departure_date: v })} testId="byf-flight-departure-date" />
          </Field>
          {data.trip_type === "round" && (
            <Field label="Return date" required>
              <DateInput value={data.return_date} onChange={(v) => setData({ return_date: v })} testId="byf-flight-return-date" />
            </Field>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="byf-flight-multi">
          {data.legs.map((leg, i) => (
            <div key={i} className="rounded-md border border-[#e2e8f0] p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 relative" data-testid={`byf-flight-leg-${i}`}>
              <Field label={`Leg ${i + 1} — From`} required>
                <AirportInput value={leg.from} onChange={(v) => setLeg(i, { from: v })} testId={`byf-flight-leg-${i}-from`} placeholder="Riyadh – RUH" />
              </Field>
              <Field label="To" required>
                <AirportInput value={leg.to} onChange={(v) => setLeg(i, { to: v })} testId={`byf-flight-leg-${i}-to`} placeholder="Paris – CDG" />
              </Field>
              <Field label="Date" required>
                <DateInput value={leg.date} onChange={(v) => setLeg(i, { date: v })} testId={`byf-flight-leg-${i}-date`} />
              </Field>
              {data.legs.length > 1 && (
                <button type="button" data-testid={`byf-flight-leg-${i}-remove`} onClick={() => removeLeg(i)} className="absolute top-1 right-1 text-xs text-[#dc2626] hover:underline">Remove</button>
              )}
            </div>
          ))}
          {data.legs.length < 3 && (
            <button type="button" data-testid="byf-flight-add-leg" onClick={addLeg} className="inline-flex items-center gap-1.5 text-sm text-[#2563eb] hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add another leg
            </button>
          )}
        </div>
      )}
    </>
  );
}

function DepartureMismatchWarning({ applyingFromLabel, onDismiss, onChange }) {
  return (
    <div data-testid="byf-flight-departure-mismatch" className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <div className="flex items-start gap-2">
        <span aria-hidden>⚠️</span>
        <div className="flex-1">
          <div>Your departure city is in a different country than where you&apos;re applying from{applyingFromLabel ? ` (${applyingFromLabel})` : ""}. Is this correct?</div>
          <div className="mt-1.5 flex gap-2 flex-wrap">
            <button type="button" data-testid="byf-flight-departure-mismatch-confirm" onClick={onDismiss} className="px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold">Yes, continue</button>
            <button type="button" data-testid="byf-flight-departure-mismatch-change" onClick={onChange} className="px-2 py-1 rounded border border-amber-400 bg-white hover:bg-amber-100 text-amber-900 font-semibold">Change departure city</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HotelFields({ data, setData, flight }) {
  const entries = data.entries || [];
  const { start: fStart, end: fEnd } = flightDateRange(flight);
  const [dismissed, setDismissed] = useState({});

  // Suggest hotel cities from flight destination(s). Only auto-fill when:
  //  - the flight destination resolves to a real airport (i.e. user has selected from suggestions, not mid-typing)
  //  - all current entries are empty (don't overwrite manual edits)
  useEffect(() => {
    if (!flight) return;
    const dests = [];
    if (flight.trip_type === "multi") {
      (flight.legs || []).forEach((leg) => {
        const a = findAirportByValue(leg?.to);
        if (a) dests.push(`${a.city} – ${a.iata}`);
      });
    } else {
      const a = findAirportByValue(flight.to);
      if (a) dests.push(`${a.city} – ${a.iata}`);
    }
    if (dests.length === 0) return;
    const allEmpty = entries.every((e) => !e.city && !e.check_in && !e.check_out && !e.preferences);
    if (!allEmpty) return;
    const next = dests.slice(0, 5).map((city) => ({ city, check_in: "", check_out: "", preferences: "" }));
    setData({ entries: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight]);

  const setEntry = (i, patch) => {
    const arr = [...entries]; arr[i] = { ...arr[i], ...patch }; setData({ entries: arr });
    // Re-show a dismissed warning when its date changes
    if ("check_in" in patch || "check_out" in patch) {
      setDismissed((d) => {
        const next = { ...d };
        if ("check_in" in patch) delete next[`${i}-ci`];
        if ("check_out" in patch) delete next[`${i}-co`];
        return next;
      });
    }
  };
  const addEntry = () => { if (entries.length < 5) setData({ entries: [...entries, { city: "", check_in: "", check_out: "", preferences: "" }] }); };
  const removeEntry = (i) => { setData({ entries: entries.filter((_, idx) => idx !== i) }); };

  // Coverage check: hotels together should cover the full flight period.
  // Dismissal keys embed the involved dates, so warnings auto-reappear when dates change.
  const allDatesFilled = entries.length > 0 && entries.every((e) => e.check_in && e.check_out);
  let coverageIssues = null;
  if (fStart && fEnd && allDatesFilled) {
    coverageIssues = [];
    const first = entries[0];
    const last = entries[entries.length - 1];
    if (first.check_in > fStart) {
      const n = nightsBetween(fStart, first.check_in);
      coverageIssues.push({
        key: `start:${fStart}:${first.check_in}`,
        testId: "byf-hotel-coverage-start-warning",
        text: `Your flight arrives ${shortDate(fStart)} but your first hotel starts ${shortDate(first.check_in)} — ${n} ${n === 1 ? "night" : "nights"} not covered. Is this correct?`,
      });
    }
    if (last.check_out < fEnd) {
      const n = nightsBetween(last.check_out, fEnd);
      coverageIssues.push({
        key: `end:${last.check_out}:${fEnd}`,
        testId: "byf-hotel-coverage-end-warning",
        text: `Your last hotel ends ${shortDate(last.check_out)} but your return flight is ${shortDate(fEnd)} — ${n} ${n === 1 ? "night" : "nights"} not covered. Add another city or extend your stay?`,
      });
    }
    for (let i = 0; i < entries.length - 1; i++) {
      if (entries[i].check_out < entries[i + 1].check_in) {
        const g = nightsBetween(entries[i].check_out, entries[i + 1].check_in);
        coverageIssues.push({
          key: `gap:${i}:${entries[i].check_out}:${entries[i + 1].check_in}`,
          testId: `byf-hotel-coverage-gap-${i}-warning`,
          text: `There is a ${g}-night gap between Hotel ${i + 1} (ends ${shortDate(entries[i].check_out)}) and Hotel ${i + 2} (starts ${shortDate(entries[i + 1].check_in)}).`,
        });
      }
    }
  }

  // Round-trip last-city check
  const lastMismatch = lastCityMismatch(entries, "check_out", flight);

  return (
    <div className="space-y-4" data-testid="byf-hotel-entries">
      {entries.map((entry, i) => (
        <div key={i} className="rounded-md border border-[#e2e8f0] p-3 relative" data-testid={`byf-hotel-entry-${i}`}>
          {entries.length > 1 && (
            <button type="button" data-testid={`byf-hotel-entry-${i}-remove`} onClick={() => removeEntry(i)} className="absolute top-1 right-2 text-xs text-[#dc2626] hover:underline">Remove</button>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={`Hotel ${i + 1} — City / airport`} required>
              <AirportInput value={entry.city} onChange={(v) => setEntry(i, { city: v })} testId={`byf-hotel-entry-${i}-city`} placeholder="Paris – CDG" />
            </Field>
            <Field label="Hotel preference" hint="Optional">
              <input data-testid={`byf-hotel-entry-${i}-preferences`} className="byf-inp" value={entry.preferences} onChange={(e) => setEntry(i, { preferences: e.target.value })} placeholder="3-star min, central" />
            </Field>
            <div>
              <Field label="Check-in date" required>
                <DateInput value={entry.check_in} onChange={(v) => setEntry(i, { check_in: v })} testId={`byf-hotel-entry-${i}-checkin`} />
              </Field>
              {fStart && entry.check_in && entry.check_in < fStart && !dismissed[`${i}-ci`] && (
                <SoftWarning testId={`byf-hotel-entry-${i}-checkin-warning`} onConfirm={() => setDismissed((d) => ({ ...d, [`${i}-ci`]: true }))}>
                  Hotel check-in ({shortDate(entry.check_in)}) is before your flight arrival ({shortDate(fStart)}). Is this correct?
                </SoftWarning>
              )}
            </div>
            <div>
              <Field label="Check-out date" required>
                <DateInput value={entry.check_out} onChange={(v) => setEntry(i, { check_out: v })} testId={`byf-hotel-entry-${i}-checkout`} />
              </Field>
              {fEnd && entry.check_out && entry.check_out > fEnd && !dismissed[`${i}-co`] && (
                <SoftWarning testId={`byf-hotel-entry-${i}-checkout-warning`} onConfirm={() => setDismissed((d) => ({ ...d, [`${i}-co`]: true }))}>
                  Hotel check-out ({shortDate(entry.check_out)}) is after your return flight ({shortDate(fEnd)}). Is this correct?
                </SoftWarning>
              )}
            </div>
          </div>
        </div>
      ))}
      {entries.length < 5 && (
        <button type="button" data-testid="byf-hotel-add-entry" onClick={addEntry} className="inline-flex items-center gap-1.5 text-sm text-[#2563eb] hover:underline">
          <Plus className="w-3.5 h-3.5" /> Add another city
        </button>
      )}
      {coverageIssues && coverageIssues.length === 0 && (
        <MatchedBanner testId="byf-hotel-coverage-matched">
          Matched — your hotels cover your full trip ({shortDate(fStart)} – {shortDate(fEnd)})
        </MatchedBanner>
      )}
      {coverageIssues && coverageIssues.filter((iss) => !dismissed[iss.key]).map((iss) => (
        <SoftWarning key={iss.key} testId={iss.testId} onConfirm={() => setDismissed((d) => ({ ...d, [iss.key]: true }))}>
          {iss.text}
        </SoftWarning>
      ))}
      {lastMismatch && !dismissed[lastMismatch.key] && (
        <SoftWarning testId="byf-hotel-lastcity-warning" onConfirm={() => setDismissed((d) => ({ ...d, [lastMismatch.key]: true }))}>
          Your trip ends in {lastMismatch.lastCity}, but your return flight departs from {lastMismatch.destCity}. Is this correct?
        </SoftWarning>
      )}
    </div>
  );
}

function CarFields({ data, setData, fallbackDriver }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Pick-up city" required>
        <input data-testid="byf-car-pickup-city" className="byf-inp" value={data.pickup_city} onChange={(e) => setData({ pickup_city: e.target.value })} />
      </Field>
      <Field label="Driver's full name" required hint="Pre-filled from full name; editable">
        <input data-testid="byf-car-driver" className="byf-inp" value={data.driver_name || fallbackDriver || ""} onChange={(e) => setData({ driver_name: e.target.value })} />
      </Field>
      <Field label="Pick-up date" required>
        <DateInput value={data.pickup_date} onChange={(v) => setData({ pickup_date: v })} testId="byf-car-pickup-date" />
      </Field>
      <Field label="Drop-off date" required>
        <DateInput value={data.dropoff_date} onChange={(v) => setData({ dropoff_date: v })} testId="byf-car-dropoff-date" />
      </Field>
    </div>
  );
}

function CoverFields({ data, setData }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Purpose of travel" required>
          <select data-testid="byf-cover-purpose" className="byf-inp" value={data.purpose} onChange={(e) => setData({ purpose: e.target.value })}>
            <option value="">Select…</option>
            <option>Tourism</option><option>Business</option><option>Family Visit</option><option>Medical</option><option>Other</option>
          </select>
        </Field>
        {data.purpose === "Other" && (
          <Field label="Specify" required>
            <input data-testid="byf-cover-other" className="byf-inp" value={data.other_purpose} onChange={(e) => setData({ other_purpose: e.target.value })} />
          </Field>
        )}
        <Field label="Employment status" required>
          <select data-testid="byf-cover-employment" className="byf-inp" value={data.employment_status} onChange={(e) => setData({ employment_status: e.target.value })}>
            <option value="">Select…</option>
            <option>Employed</option><option>Self-employed</option><option>Student</option><option>Retired</option><option>Other</option>
          </select>
        </Field>
        <Field label="Travel from" required>
          <DateInput value={data.travel_from} onChange={(v) => setData({ travel_from: v })} testId="byf-cover-from" />
        </Field>
        <Field label="Travel to" required>
          <DateInput value={data.travel_to} onChange={(v) => setData({ travel_to: v })} testId="byf-cover-to" />
        </Field>
      </div>
    </>
  );
}

function ItineraryFields({ data, setData, flight, hotel }) {
  const entries = data.entries || [];
  const [dismissed, setDismissed] = useState({});

  // Reference hierarchy: flight dates (P1) → hotel period when no flight but hotel selected (P2) → none (P3)
  const flightRange = flightDateRange(flight);
  let refStart = "", refEnd = "", refLabel = "";
  if (flight && flightRange.start && flightRange.end) {
    refStart = flightRange.start; refEnd = flightRange.end; refLabel = "flight";
  } else if (!flight && hotel) {
    const hEntries = hotel.entries || [];
    const firstH = hEntries[0];
    const lastH = hEntries[hEntries.length - 1];
    if (firstH?.check_in && lastH?.check_out) {
      refStart = firstH.check_in; refEnd = lastH.check_out; refLabel = "hotel";
    }
  }

  // Round-trip last-city check (uses flight directly, not the ref hierarchy)
  const lastMismatch = lastCityMismatch(entries, "to", flight);

  // Auto-populate cities: prefer hotel cities, else flight destination(s). Only when all entries are empty.
  useEffect(() => {
    const allEmpty = entries.every((e) => !e.city && !e.from && !e.to);
    if (!allEmpty) return;
    let cities = (hotel?.entries || []).map((h) => h.city).filter((c) => c && findAirportByValue(c));
    if (cities.length === 0 && flight) {
      if (flight.trip_type === "multi") {
        (flight.legs || []).forEach((leg) => {
          const a = findAirportByValue(leg?.to);
          if (a) cities.push(`${a.city} – ${a.iata}`);
        });
      } else {
        const a = findAirportByValue(flight.to);
        if (a) cities.push(`${a.city} – ${a.iata}`);
      }
    }
    if (cities.length === 0) return;
    setData({ entries: cities.slice(0, 6).map((city) => ({ city, date_mode: "arrange", from: "", to: "" })) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight, hotel]);

  const setEntry = (i, patch) => {
    const arr = [...entries]; arr[i] = { ...arr[i], ...patch }; setData({ entries: arr });
    if ("from" in patch || "to" in patch) {
      setDismissed((d) => { const next = { ...d }; delete next[i]; return next; });
    }
  };
  const addEntry = () => { if (entries.length < 6) setData({ entries: [...entries, { city: "", date_mode: "arrange", from: "", to: "" }] }); };
  const removeEntry = (i) => { setData({ entries: entries.filter((_, idx) => idx !== i) }); };

  // Coverage summary (below last city card)
  const cityDisplay = (v) => (v || "").split("–")[0].trim();
  const customEntries = entries.filter((e) => e.date_mode === "custom");
  const allArrange = entries.length > 0 && customEntries.length === 0;
  const allCustom = entries.length > 0 && customEntries.length === entries.length;
  const completeCustom = customEntries.filter((e) => e.from && e.to);
  const totalNights = completeCustom.reduce((s, e) => s + nightsBetween(e.from, e.to), 0);
  const breakdown = completeCustom.map((e) => `${nightsBetween(e.from, e.to)} ${cityDisplay(e.city) || "City"}`).join(" · ");
  const refNights = refStart && refEnd ? nightsBetween(refStart, refEnd) : 0;

  // Full-coverage checks only when ALL cities are custom with complete dates (mixed mode → per-city checks only)
  let itinIssues = null;
  let itinMatched = false;
  if (refStart && refEnd && allCustom && completeCustom.length === entries.length) {
    itinIssues = [];
    for (let i = 0; i < entries.length - 1; i++) {
      if (entries[i].to < entries[i + 1].from) {
        itinIssues.push({
          key: `igap:${i}:${entries[i].to}:${entries[i + 1].from}`,
          testId: `byf-itin-coverage-gap-${i}-warning`,
          text: `There is a gap between ${cityDisplay(entries[i].city) || `City ${i + 1}`} (ends ${shortDate(entries[i].to)}) and ${cityDisplay(entries[i + 1].city) || `City ${i + 2}`} (starts ${shortDate(entries[i + 1].from)}).`,
        });
      }
    }
    if (totalNights < refNights) {
      const missing = refNights - totalNights;
      itinIssues.push({
        key: `unplanned:${totalNights}:${refNights}`,
        testId: "byf-itin-coverage-unplanned-warning",
        text: `Your itinerary covers ${totalNights} of ${refNights} nights. ${missing} ${missing === 1 ? "night is" : "nights are"} unplanned.`,
      });
    }
    const withinPeriod = entries.every((e) => e.from >= refStart && e.to <= refEnd);
    itinMatched = itinIssues.length === 0 && totalNights === refNights && withinPeriod;
  }

  return (
    <div className="space-y-4" data-testid="byf-itin-entries">
      {entries.map((entry, i) => {
        const nights = entry.date_mode === "custom" ? nightsBetween(entry.from, entry.to) : 0;
        const outside = entry.date_mode === "custom" && entry.from && entry.to && refStart && refEnd && (entry.from < refStart || entry.to > refEnd);
        return (
          <div key={i} className="rounded-md border border-[#e2e8f0] p-3 relative" data-testid={`byf-itin-entry-${i}`}>
            {entries.length > 1 && (
              <button type="button" data-testid={`byf-itin-entry-${i}-remove`} onClick={() => removeEntry(i)} className="absolute top-1 right-2 text-xs text-[#dc2626] hover:underline">Remove</button>
            )}
            <Field label={`City ${i + 1}`} required>
              <AirportInput value={entry.city} onChange={(v) => setEntry(i, { city: v })} testId={`byf-itin-entry-${i}-city`} placeholder="Paris – CDG" />
            </Field>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition ${entry.date_mode === "arrange" ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white hover:border-[#cbd5e1]"}`}>
                <input type="radio" name={`byf-itin-mode-${i}`} checked={entry.date_mode === "arrange"} onChange={() => setEntry(i, { date_mode: "arrange" })} data-testid={`byf-itin-entry-${i}-mode-arrange`} className="accent-[#2563eb]" />
                SchengenFile will arrange
              </label>
              <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition ${entry.date_mode === "custom" ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white hover:border-[#cbd5e1]"}`}>
                <input type="radio" name={`byf-itin-mode-${i}`} checked={entry.date_mode === "custom"} onChange={() => setEntry(i, { date_mode: "custom" })} data-testid={`byf-itin-entry-${i}-mode-custom`} className="accent-[#2563eb]" />
                I&apos;ll set the dates
              </label>
            </div>
            {entry.date_mode === "arrange" ? (
              <p className="mt-2 text-xs text-[#94a3b8]" data-testid={`byf-itin-entry-${i}-arrange-note`}>Our team will plan the optimal dates based on your trip</p>
            ) : (
              <div className="mt-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="From date" required>
                    <DateInput value={entry.from} onChange={(v) => setEntry(i, { from: v })} testId={`byf-itin-entry-${i}-from`} />
                  </Field>
                  <Field label="To date" required>
                    <DateInput value={entry.to} onChange={(v) => setEntry(i, { to: v })} testId={`byf-itin-entry-${i}-to`} />
                  </Field>
                </div>
                {nights > 0 && (
                  <div className="mt-1.5 text-xs font-semibold text-[#2563eb]" data-testid={`byf-itin-entry-${i}-nights`}>{nights} {nights === 1 ? "night" : "nights"}</div>
                )}
                {outside && !dismissed[i] && (
                  <SoftWarning testId={`byf-itin-entry-${i}-dates-warning`} onConfirm={() => setDismissed((d) => ({ ...d, [i]: true }))}>
                    These dates are outside your {refLabel} period ({shortDate(refStart)} – {shortDate(refEnd)}). Is this correct?
                  </SoftWarning>
                )}
              </div>
            )}
          </div>
        );
      })}
      {entries.length < 6 && (
        <button type="button" data-testid="byf-itin-add-entry" onClick={addEntry} className="inline-flex items-center gap-1.5 text-sm text-[#2563eb] hover:underline">
          <Plus className="w-3.5 h-3.5" /> Add another city
        </button>
      )}
      {completeCustom.length > 0 && (
        <div className="pt-2 border-t border-dashed border-[#e2e8f0] text-sm font-semibold text-[#0f1b2d]" data-testid="byf-itin-total">
          Total: <span className="text-[#2563eb]">{totalNights} {totalNights === 1 ? "night" : "nights"}</span>
          {breakdown && <span className="font-normal text-[#64748b]"> ({breakdown})</span>}
        </div>
      )}
      {itinMatched && (
        <MatchedBanner testId="byf-itin-coverage-matched">
          Matched — your itinerary covers your full trip ({shortDate(refStart)} – {shortDate(refEnd)})
        </MatchedBanner>
      )}
      {itinIssues && itinIssues.filter((iss) => !dismissed[iss.key]).map((iss) => (
        <SoftWarning key={iss.key} testId={iss.testId} onConfirm={() => setDismissed((d) => ({ ...d, [iss.key]: true }))}>
          {iss.text}
        </SoftWarning>
      ))}
      {lastMismatch && !dismissed[lastMismatch.key] && (
        <SoftWarning testId="byf-itin-lastcity-warning" onConfirm={() => setDismissed((d) => ({ ...d, [lastMismatch.key]: true }))}>
          Your trip ends in {lastMismatch.lastCity}, but your return flight departs from {lastMismatch.destCity}. Is this correct?
        </SoftWarning>
      )}
      {refStart && refEnd && allArrange && (
        <p className="text-xs text-[#64748b]" data-testid="byf-itin-arrange-note">
          Our team will plan your itinerary to match your {refLabel} dates ({shortDate(refStart)} – {shortDate(refEnd)}).
        </p>
      )}
      <Field label="Activities / interests" hint="Optional">
        <textarea rows={2} data-testid="byf-itin-activities" className="byf-inp" value={data.activities} onChange={(e) => setData({ activities: e.target.value })} placeholder="museums, food, history" />
      </Field>
    </div>
  );
}

function InsuranceFields({ dobs, setDob }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {dobs.map((v, i) => (
        <Field key={i} label={`Applicant ${i + 1} — date of birth`} required>
          <DateInput value={v} onChange={(val) => setDob(i, val)} testId={`byf-insurance-dob-${i}`} />
        </Field>
      ))}
    </div>
  );
}

function FormSection({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg p-5 sm:p-6 space-y-4" data-testid={`byf-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#2563eb]" />
        <h3 className="font-display font-bold text-base text-[#0f1b2d]">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-[#475569] -mt-2">{subtitle}</p>}
      {children}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#0f1b2d]">
        {label} {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-2 text-[10px] text-[#64748b] font-normal">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

/* ==================== Right summary ==================== */
function travellerCountsLabel(travellers) {
  const counts = { Adult: 0, Child: 0, Infant: 0 };
  (travellers || []).forEach((t) => { counts[ageCategory(t.dob) || "Adult"]++; });
  const parts = [];
  if (counts.Adult) parts.push(`${counts.Adult} ${counts.Adult === 1 ? "Adult" : "Adults"}`);
  if (counts.Child) parts.push(`${counts.Child} ${counts.Child === 1 ? "Child" : "Children"}`);
  if (counts.Infant) parts.push(`${counts.Infant} ${counts.Infant === 1 ? "Infant" : "Infants"}`);
  return parts.join(" · ");
}

function DeliveryPromise({ isExpress, testId, className = "" }) {
  if (!isExpress) {
    return (
      <div data-testid={testId} className={`text-[11px] text-[#94a3b8] ${className}`}>
        Delivered within 24 hours
      </div>
    );
  }
  const now = new Date();
  const d = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const day = d.getDate() === now.getDate() ? "today" : "tomorrow";
  return (
    <div data-testid={testId} className={`text-[11px] font-semibold text-amber-600 flex items-center gap-1 ${className}`}>
      <Zap className="w-3 h-3 flex-shrink-0" /> Delivered by {hh}:{mm} {day}
    </div>
  );
}

function SummaryDetails({ selectedDocs, selectedAddons, applicants, travellers, isExpress, setIsExpress, docsPerApplicant, addonsPerApplicant, docsSubtotal, addonsSubtotal, expressSurcharge, total, hotelCityCount, idSuffix = "" }) {
  const s = idSuffix;
  return (
    <div className="space-y-4">
      {selectedDocs.length === 0 && selectedAddons.length === 0 ? (
        <p className="text-sm text-[#64748b]" data-testid={`byf-summary-empty${s}`}>No documents selected yet. Pick at least one from the list to continue.</p>
      ) : (
        <ul className="space-y-2" data-testid={`byf-summary-list${s}`}>
          {selectedDocs.map((id) => {
            const d = BUILD_DOCS_BY_ID[id];
            const isHotel = id === "hotel" && (hotelCityCount || 0) > 0;
            return (
              <li key={id} className="flex items-center justify-between gap-3 text-sm" data-testid={`byf-summary-item-${id}${s}`}>
                <span className="text-[#0f1b2d] truncate">{d.emoji} {d.short}{isHotel ? ` · ${hotelCityCount} ${hotelCityCount === 1 ? "city" : "cities"}` : ""}</span>
                <span className="font-semibold text-[#0f1b2d] flex-shrink-0">${d.price}</span>
              </li>
            );
          })}
          {selectedAddons.map((id) => {
            const a = ADD_ONS.find((x) => x.id === id);
            return (
              <li key={id} className="flex items-center justify-between gap-3 text-sm" data-testid={`byf-summary-addon-${id}${s}`}>
                <span className="text-[#0f1b2d] truncate">{ADDON_EMOJI[id]} {a.short || a.name}</span>
                <span className="font-semibold text-[#0f1b2d] flex-shrink-0">${a.price}</span>
              </li>
            );
          })}
        </ul>
      )}

      <div className="pt-4 border-t border-[#e2e8f0] flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-[#0f1b2d] uppercase tracking-widest">Travellers</span>
        <span data-testid={`byf-summary-travellers${s}`} className="text-sm font-semibold text-[#0f1b2d] text-right">
          {applicants} <span className="font-normal text-[#64748b]">({travellerCountsLabel(travellers)})</span>
        </span>
      </div>

      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="flex items-center gap-2 text-sm text-[#0f1b2d]"><Zap className="w-4 h-4 text-amber-500" /> Express delivery (3h) <span className="text-[11px] text-[#64748b]">+$10/doc</span></span>
        <button type="button" role="switch" aria-checked={isExpress} onClick={() => setIsExpress((v) => !v)} data-testid={`byf-express-toggle${s}`}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${isExpress ? "bg-[#2563eb]" : "bg-[#cbd5e1]"}`}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${isExpress ? "translate-x-4" : "translate-x-0.5"}`} />
        </button>
      </label>
      <DeliveryPromise isExpress={isExpress} testId={`byf-delivery-promise${s}`} />

      <div className="pt-4 border-t border-[#e2e8f0] space-y-1.5 text-sm">
        <SummaryRow label={`Documents (per traveller)`} value={`$${docsPerApplicant}`} testId={`byf-row-docs-pp${s}`} />
        {addonsPerApplicant > 0 && (
          <SummaryRow label="Add-ons (per traveller)" value={`$${addonsPerApplicant}`} testId={`byf-row-addons-pp${s}`} />
        )}
        <SummaryRow label={`× ${applicants} ${applicants === 1 ? "traveller" : "travellers"}`} value={`$${docsSubtotal + addonsSubtotal}`} testId={`byf-row-multiplied${s}`} />
        {isExpress && <SummaryRow label="Express surcharge" value={`+$${expressSurcharge}`} testId={`byf-row-express${s}`} />}
      </div>

      <div className="pt-3 border-t-2 border-[#e2e8f0] flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-[#64748b] font-semibold">Total</span>
        <span data-testid={`byf-total${s}`} className="font-display font-black text-3xl text-[#0f1b2d]">${total}</span>
      </div>
    </div>
  );
}

function OrderSummary(props) {
  const { step, canContinue, submitting, submitError, onContinue, onProceed, total } = props;
  const [sheetOpen, setSheetOpen] = useState(false);

  const cta = step === "select"
    ? { label: "Continue", onClick: onContinue, disabled: !canContinue || submitting, testId: "byf-continue-btn" }
    : { label: submitting ? "Creating order…" : "Proceed to payment", onClick: onProceed, disabled: submitting, testId: "byf-proceed-payment-btn" };

  return (
    <>
      {/* Desktop / in-flow card (sticky on lg) */}
      <div className="lg:sticky lg:top-24 rounded-xl border border-[#e2e8f0] bg-white shadow-sm overflow-hidden" data-testid="byf-order-summary">
        <div className="px-5 py-4 border-b border-[#e2e8f0] bg-[#f8fafc]">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Live Summary</div>
          <h3 className="font-display font-bold text-lg text-[#0f1b2d]">Your custom file</h3>
        </div>
        <div className="p-5 space-y-4">
          <SummaryDetails {...props} />

          <button type="button" onClick={cta.onClick} disabled={cta.disabled}
            data-testid={cta.testId}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-md transition-all">
            {cta.label} <ArrowRight className="w-4 h-4" />
          </button>

          {submitError && (
            <p className="text-sm text-red-600" data-testid="byf-submit-error">{submitError}</p>
          )}

          <div className="pt-3 text-[11px] text-[#64748b] flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Secure payment via Stripe. Refund if not delivered on time.</span>
          </div>
          <div className="text-[11px] text-[#64748b] flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>3D Secure protected. No card details stored.</span>
          </div>
        </div>
      </div>

      {/* Mobile fixed bottom bar + slide-up sheet */}
      {sheetOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setSheetOpen(false)} data-testid="byf-mobile-sheet-backdrop" aria-hidden />
      )}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-50">
        {sheetOpen && (
          <div className="bg-white border-t border-[#e2e8f0] rounded-t-xl max-h-[60vh] overflow-y-auto px-5 py-4 mobile-sheet-enter shadow-[0_-8px_24px_rgba(0,0,0,0.12)]" data-testid="byf-mobile-sheet">
            <SummaryDetails {...props} idSuffix="-mobile" />
            {submitError && (
              <p className="mt-3 text-sm text-red-600" data-testid="byf-submit-error-mobile">{submitError}</p>
            )}
          </div>
        )}
        <div className="bg-white border-t border-[#e2e8f0] shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-testid="byf-mobile-bar">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <button type="button" onClick={() => setSheetOpen((v) => !v)} data-testid="byf-mobile-total" className="text-left">
              <DeliveryPromise isExpress={props.isExpress} testId="byf-delivery-promise-bar" className="mb-0.5" />
              <span className="text-[11px] text-[#64748b] font-semibold uppercase tracking-widest flex items-center gap-1">
                Total <ChevronUp className={`w-3.5 h-3.5 transition-transform ${sheetOpen ? "rotate-180" : ""}`} />
              </span>
              <div className="font-display font-black text-xl text-[#0f1b2d] leading-tight">${total}</div>
            </button>
            <button type="button" onClick={() => { setSheetOpen(false); cta.onClick(); }} disabled={cta.disabled}
              data-testid={`${cta.testId}-mobile`}
              className="inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-4 py-2.5 rounded-md text-sm transition-all">
              {cta.label} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ==================== Card primitives ==================== */
function DocSelectorCard({ testIdRoot, checked, onToggle, emoji, name, description, price, preview }) {
  return (
    <button
      type="button"
      data-testid={testIdRoot}
      aria-pressed={checked}
      onClick={onToggle}
      className={`text-left relative rounded-xl border-2 p-4 sm:p-5 transition-all flex flex-col gap-3 ${checked ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white hover:border-[#94a3b8]"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="text-2xl flex-shrink-0" aria-hidden>{emoji}</div>
          <div className="min-w-0">
            <div className="font-semibold text-[#0f1b2d] text-sm leading-snug">{name}</div>
            <div className="mt-0.5 text-xs text-[#64748b] leading-snug">{description}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <div className={`w-5 h-5 rounded-md grid place-items-center transition ${checked ? "bg-[#2563eb] text-white" : "border-2 border-[#cbd5e1] bg-white"}`} data-testid={`${testIdRoot}-checkbox`}>
            {checked && <Check className="w-3 h-3" strokeWidth={3} />}
          </div>
          <div className="font-display font-bold text-lg text-[#0f1b2d]" data-testid={`${testIdRoot}-price`}>${price}</div>
        </div>
      </div>
      {/* Preview tile — natural height, centered, no extra whitespace */}
      <div className="flex justify-center">
        <PreviewCard item={{ title: preview.title, package_contents: preview.package_contents }} />
      </div>
    </button>
  );
}

function SummaryRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between text-[#475569]" data-testid={testId}>
      <span>{label}</span>
      <span className="font-semibold text-[#0f1b2d]">{value}</span>
    </div>
  );
}
